import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { Plus, WarningCircle, CalendarCheck, ListChecks } from "@phosphor-icons/react";
import CrmLeadDrawer from "@/components/crm/CrmLeadDrawer";
import NewLeadDialog from "@/components/crm/NewLeadDialog";
import { CRM_STAGES, STAGE_MAP, fmtMoney, serviceLabel, sourceLabel } from "@/lib/crm";

// Mobile-first VA CRM workspace — own leads only, stage pills + card list.
export default function VACRM() {
  const [board, setBoard] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [err, setErr] = useState("");
  const [activeStage, setActiveStage] = useState("all");
  const [openLead, setOpenLead] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try {
      const [b, t] = await Promise.all([api.get("/crm/board"), api.get("/crm/tasks")]);
      setBoard(b.data);
      setTasks(t.data);
    } catch (e) { setErr(getErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const stages = board?.stages || [];
  const visible = activeStage === "all"
    ? stages.flatMap((s) => s.leads)
    : (stages.find((s) => s.stage === activeStage)?.leads || []);

  return (
    <div className="p-4 md:p-8" data-testid="va-crm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-label">CRM Pipeline</div>
          <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">My pipeline</h1>
          <p className="mt-1 text-sm text-[#4B5563]">
            Open value: <strong className="text-emerald-700">{board ? fmtMoney(board.total_open_value) : "…"}</strong>
          </p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="va-crm-new-lead-btn"
          className="inline-flex h-10 items-center gap-1.5 bg-[#C84B31] px-4 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#A03A24]">
          <Plus size={14} weight="bold" /> New lead
        </button>
      </div>

      {err && <div className="mb-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="va-crm-error">{err}</div>}

      {/* Today's tasks (FR-18) */}
      {tasks && (tasks.overdue.length > 0 || tasks.due_today.length > 0 || tasks.missing_next_action.length > 0) && (
        <div className="mb-4 border border-[#030712] bg-white" data-testid="va-crm-tasks">
          <div className="flex items-center gap-2 border-b border-[#E5E7EB] px-4 py-2 font-mono-label">
            <ListChecks size={14} /> Today's tasks
            {tasks.overdue.length > 0 && (
              <span className="bg-red-600 px-2 py-0.5 text-[9px] font-bold text-white" data-testid="va-crm-overdue-count">
                {tasks.overdue.length} OVERDUE
              </span>
            )}
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            {tasks.overdue.map((l) => (
              <button key={l.lead_id} onClick={() => setOpenLead(l.lead_id)} data-testid={`va-task-overdue-${l.lead_id}`}
                className="flex w-full items-center justify-between gap-2 border-l-4 border-red-600 px-4 py-2 text-left text-xs hover:bg-red-50">
                <span className="flex items-center gap-1.5 font-semibold"><WarningCircle size={14} className="text-red-600" weight="fill" /> {l.prospect_name}</span>
                <span className="font-mono text-red-700">was due {l.next_action_date}</span>
              </button>
            ))}
            {tasks.due_today.map((l) => (
              <button key={l.lead_id} onClick={() => setOpenLead(l.lead_id)} data-testid={`va-task-today-${l.lead_id}`}
                className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-xs hover:bg-[#F9FAFB]">
                <span className="flex items-center gap-1.5 font-semibold"><CalendarCheck size={14} className="text-[#0044FF]" /> {l.prospect_name}</span>
                <span className="font-mono text-[#4B5563]">due today</span>
              </button>
            ))}
            {tasks.missing_next_action.length > 0 && (
              <div className="px-4 py-2 text-[11px] text-amber-700" data-testid="va-crm-missing-next-action">
                {tasks.missing_next_action.length} open lead{tasks.missing_next_action.length > 1 ? "s" : ""} missing a next-action date —{" "}
                <button className="underline" onClick={() => setOpenLead(tasks.missing_next_action[0].lead_id)}>fix the first one</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stage pills */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="va-crm-stage-pills">
        <button onClick={() => setActiveStage("all")} data-testid="va-crm-pill-all"
          className={`shrink-0 border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${activeStage === "all" ? "border-[#030712] bg-[#030712] text-white" : "border-[#030712]"}`}>
          All {board ? `(${stages.reduce((a, s) => a + s.count, 0)})` : ""}
        </button>
        {stages.map((s) => (
          <button key={s.stage} onClick={() => setActiveStage(s.stage)} data-testid={`va-crm-pill-${s.stage}`}
            className={`shrink-0 border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${activeStage === s.stage ? "text-white" : ""}`}
            style={activeStage === s.stage
              ? { backgroundColor: STAGE_MAP[s.stage].color, borderColor: STAGE_MAP[s.stage].color }
              : { borderColor: STAGE_MAP[s.stage].color, color: STAGE_MAP[s.stage].color }}>
            {s.label} ({s.count})
          </button>
        ))}
      </div>

      {!board ? (
        <div className="font-mono-label">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] bg-white p-10 text-center" data-testid="va-crm-empty">
          <div className="font-display text-lg font-black">No leads here</div>
          <div className="mt-1 text-sm text-[#4B5563]">Add a lead or check another stage.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="va-crm-lead-list">
          {visible.map((l) => (
            <button key={l.lead_id} onClick={() => setOpenLead(l.lead_id)} data-testid={`va-crm-card-${l.lead_id}`}
              className={`block w-full border bg-white p-3 text-left text-sm hover:border-[#030712] ${l.overdue ? "border-l-4 border-red-500" : "border-[#E5E7EB]"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold">{l.prospect_name}</div>
                <div className="flex items-center gap-1">
                  {l.overdue && <span className="bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Overdue</span>}
                  {l.sla_state === "breached" && <span className="bg-red-700 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">SLA!</span>}
                  {l.sla_state === "warning" && <span className="bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">SLA ⚠</span>}
                  <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: STAGE_MAP[l.crm_stage].color }}>
                    {STAGE_MAP[l.crm_stage].label}
                  </span>
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
                <span>{serviceLabel(l.service_type)}</span>
                <span>· {sourceLabel(l.source)}</span>
                {l.value > 0 && <span className="font-mono font-bold text-emerald-700">· {fmtMoney(l.value)}</span>}
                {l.next_action_date && <span>· Next: {l.next_action_date}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {openLead && (
        <CrmLeadDrawer leadId={openLead} role="va" onClose={() => setOpenLead(null)} onChanged={load} />
      )}
      {showNew && (
        <NewLeadDialog role="va" onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}
